import { useEffect } from 'react';
import { Text } from '@teachstonellc/component-library';
import ReportLoader from 'components/Reports/ReportLoader';
import { Navigate } from 'react-router-dom';
import i18n from 'helpers/i18n';
import useAsync from 'hooks/useAsync';
import useCurrentUser from 'hooks/useCurrentUser';
import usePermissions from 'hooks/usePermissions';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { useRouteParams } from 'hooks/useRouteParams';
import { getTraining, getTransitionTestReports } from 'actions/trainings';
import { TransitionTestReports } from 'types/api/ReliabilityReport';
import MainContent from 'components/MainContent';
import PageLayout from 'components/PageLayout';
import Summary from 'components/Reports/Reliability/Summary';
import OverallChart from 'components/Reports/Reliability/Charts/OverallChart';
import AttemptsBarChart from 'components/Reports/Reliability/Charts/AttemptsBarChart';
import { ChartsContainer } from 'components/Reports/Reliability/Charts/Styled';
import ReportTable from 'components/Reports/Reliability/Table';
import messages from 'components/Trainings/View/Details/ReliabilityReport/messages';

export default function TransitionTestReport() {
  const { trainingId: id } = useRouteParams<{ trainingId: string }>();
  const trainingId = Number(id);
  const currentUser = useCurrentUser();
  const permissions = usePermissions('trainer_dashboard', 'trainings');
  const { run, data: training, ...trainingStatus } = useAsync();
  const {
    run: runReport,
    data: report,
    ...reportStatus
  } = useAsync<TransitionTestReports>();
  useDocumentTitle(i18n.ft(messages.combinedReportTitle));

  useEffect(() => {
    run(getTraining(trainingId));
  }, [run, trainingId]);

  const canAccessReport =
    training?.combined_test_report &&
    training.status === 'completed' &&
    (permissions.manage || training.trainer.id === currentUser.id);
  const canView = canAccessReport && Boolean(training.test_type);

  useEffect(() => {
    if (canView) runReport(getTransitionTestReports(trainingId));
  }, [canView, runReport, trainingId]);

  if (trainingStatus.isPending)
    return <ReportLoader label={i18n.ft(messages.loading)} />;
  if (trainingStatus.isError || !canAccessReport)
    return <Navigate replace to="/trainings" />;
  if (canView && reportStatus.isPending)
    return <ReportLoader label={i18n.ft(messages.loading)} />;
  if (!canView || reportStatus.isError)
    return (
      <div role="alert" className="rounded bg-red-50 p-4 text-red-800">
        {i18n.ft(messages.errorMessage)}{' '}
        <a href="mailto:contact@teachstone.com">contact@teachstone.com</a>
      </div>
    );

  const sections = (['reliability', 'knowledge'] as const).map(testType => ({
    id: testType,
    label: i18n.ft(
      testType === 'reliability'
        ? messages.reliabilityLabel
        : messages.knowledgeLabel
    ),
    abbreviation: 'PKK3' as const,
    data: report[testType]
  }));

  return (
    <MainContent maxWidth={1024}>
      <PageLayout>
        <Text as="h1" className="mb-4 text-2xl font-bold">
          {i18n.ft(messages.combinedReportTitle)}
        </Text>
        <Summary
          training={training}
          report={{ PKK3: report.reliability }}
          totalParticipants={training.completed_participant_count}
        />
        <div>
          {(['reliability', 'knowledge'] as const).map(testType => {
            const data = report[testType];
            const attempts = [1, 2, 3].map(
              number =>
                data.total_by_attempt.find(
                  attempt => attempt.number === number
                ) ?? { passed: 0, total: 0 }
            );
            return (
              <ChartsContainer key={testType}>
                <OverallChart
                  abbreviation="PKK3"
                  testType={testType}
                  byTestType
                  totalTaken={data.total_taken}
                  totalPassed={data.total_passed}
                  attempt01={attempts[0].passed}
                  attempt02={attempts[1].passed}
                  attempt03={attempts[2].passed}
                />
                <AttemptsBarChart
                  abbreviation="PKK3"
                  testType={testType}
                  attempt01={attempts[0]}
                  attempt02={attempts[1]}
                  attempt03={attempts[2]}
                />
              </ChartsContainer>
            );
          })}
        </div>
        <ReportTable sections={sections} />
      </PageLayout>
    </MainContent>
  );
}
